import React, {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Container, Grid, ContainerVariant, ContainerPadding} from '../../components/layout';
import {Heading, Text, TextVariant, TextColor, HeadingVariant, HeadingLevel, MarginBottom} from '../../components/typography';
import {Button, ButtonVariant, ButtonSize, ButtonType} from '../../components/button';
import {Input} from '../../components/input';
import {FormControl} from '../../components/form-control';
import {SignupFeatures} from '../../components/signup-features';
import {Icons} from '../../assets/icons';
import {SuccessModal} from './components';
import {useVerificationCode, useEmailVerificationFlow, useCountdownTimer} from '../../hooks';
import {getResendButtonText} from '../../utils';

interface LocationState {
  email?: string;
  accountId?: string;
  isLogin?: boolean;
}

export const VerifyEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {email, accountId, isLogin} = (location.state as LocationState) || {};

  const {verificationCode, handleCodeChange, cleanCode, isValidCode} = useVerificationCode();

  const {
    timeLeft,
    isExpired: canResend,
    resetTimer,
  } = useCountdownTimer({
    initialSeconds: 120,
    autoStart: true,
  });

  const {
    isVerifying,
    isResending,
    error,
    showSuccessModal,
    organizationData,
    resendCode,
    verifyCode,
    setError,
    handleGoToDashboard,
    handleCloseModal,
  } = useEmailVerificationFlow(accountId, isLogin, email);

  useEffect(() => {
    if (!email || !accountId) {
      navigate('/signup', {replace: true});
    }
  }, [email, accountId, navigate]);

  const handleContinue = async () => {
    if (!isValidCode) {
      setError('Please enter a valid 6-digit verification code.');
      return;
    }
    await verifyCode(cleanCode);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleResendCode = async () => {
    await resendCode();
    resetTimer(120);
  };

  return (
    <Grid cols={{sm: 1, lg: 2}}>
      <SignupFeatures />
      <Container variant={ContainerVariant.Form} padding={ContainerPadding.MD} className="flex items-center justify-center min-h-screen">
        <div className="w-full max-w-sm mx-auto">
          <div className="mb-8">
            <Heading level={HeadingLevel.H2} variant={HeadingVariant.PAGE_TITLE} marginBottom={MarginBottom.SM}>
              Verify your Email
            </Heading>
            <Text variant={TextVariant.BODY} color={TextColor.MUTED} className="mb-4">
              We&apos;ve sent a 6-digit verification code to your mail.
            </Text>
            {email && (
              <Text variant={TextVariant.BODY} color={TextColor.PRIMARY} className="font-medium">
                {email}
              </Text>
            )}
          </div>

          <div className="space-y-6">
            <FormControl label="Enter code" error={error}>
              <Input
                type="text"
                placeholder="- - - - - -"
                value={verificationCode}
                onChange={e => handleCodeChange(e.target.value)}
                disabled={isVerifying}
                className="text-center text-xl tracking-[0.3em] font-mono"
                maxLength={11}
              />
            </FormControl>

            <div className="flex items-center justify-between">
              <Text variant={TextVariant.BODY} color={TextColor.MUTED}>
                Didn&apos;t receive the code?
              </Text>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending || !canResend}
                className="text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed">
                {getResendButtonText({
                  isResending,
                  canResend,
                  timeLeft,
                })}
              </button>
            </div>

            <Button
              type={ButtonType.BUTTON}
              variant={ButtonVariant.Primary}
              size={ButtonSize.MD}
              fullWidth
              onClick={handleContinue}
              disabled={!isValidCode}
              loading={isVerifying}
              loadingText="Verifying...">
              Continue
            </Button>

            <div className="text-center">
              <button type="button" onClick={handleGoBack} className="inline-flex items-center text-gray-600 hover:text-gray-800">
                <img src={Icons.ARROW_LEFT} alt="Back" className="w-4 h-4 mr-2" />
                Go Back
              </button>
            </div>
          </div>
        </div>
      </Container>

      {organizationData && (
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseModal}
          name={organizationData.name}
          email={organizationData.email}
          company={organizationData.company}
          portalUrl={organizationData.portalUrl}
          onGoToDashboard={handleGoToDashboard}
        />
      )}
    </Grid>
  );
};
